import type { Component } from 'vue'
import type { SchemaFormBuiltinRender } from './FaSchemaForm/types'
import {
  ElAutocomplete,
  ElCheckbox,
  ElCheckboxButton,
  ElCheckboxGroup,
  ElColorPicker,
  ElMention,
  ElOption,
  ElRadio,
  ElRadioButton,
  ElRadioGroup,
  ElRate,
  ElSelectV2,
  ElTimeSelect,
  ElTransfer,
  ElTreeSelect,
  ElUpload,
} from 'element-plus'
import { h } from 'vue'
import AppDatePicker from '@/components/AppForm/DatePicker.vue'
import AppDateRangePicker from '@/components/AppForm/DateRangePicker.vue'
import AppTimePicker from '@/components/AppForm/TimePicker.vue'

export type SchemaFieldBuiltinRender = SchemaFormBuiltinRender | 'date' | 'daterange'

export interface SchemaFieldOptionLike {
  label: string
  value: any
  disabled?: boolean
  [key: string]: any
}

type LocalComponentResolver = (name: string) => Component | string

const commonBuiltinComponentMap: Partial<Record<string, Component | string>> = {
  autocomplete: ElAutocomplete,
  cascader: 'FaCascader',
  colorPicker: ElColorPicker,
  date: AppDatePicker,
  daterange: AppDateRangePicker,
  datePicker: AppDatePicker,
  input: 'FaInput',
  inputNumber: 'FaNumberField',
  mention: ElMention,
  number: 'FaNumberField',
  rate: ElRate,
  select: 'FaSelect',
  selectV2: ElSelectV2,
  switch: 'FaSwitch',
  textarea: 'FaTextarea',
  timePicker: AppTimePicker,
  timeSelect: ElTimeSelect,
  transfer: ElTransfer,
  treeSelect: ElTreeSelect,
  upload: ElUpload,
}

export function getSchemaFieldDefaultValue(renderType: SchemaFieldBuiltinRender | string) {
  if (['checkboxGroup', 'checkboxButton', 'daterange', 'transfer'].includes(renderType)) {
    return []
  }
  if (renderType === 'switch') {
    return false
  }
  if (['cascader', 'number', 'inputNumber', 'rate', 'slider'].includes(renderType)) {
    return undefined
  }
  return ''
}

export function isSchemaFieldOptionType(renderType: SchemaFieldBuiltinRender | string) {
  return [
    'autocomplete',
    'cascader',
    'checkbox',
    'checkboxButton',
    'checkboxGroup',
    'mention',
    'radio',
    'radioButton',
    'radioGroup',
    'select',
    'selectV2',
    'transfer',
    'treeSelect',
  ].includes(renderType)
}

export function resolveSchemaFieldBuiltinComponent(
  renderType: SchemaFieldBuiltinRender | string,
  options: SchemaFieldOptionLike[],
  resolveLocalComponent: LocalComponentResolver,
  overrides: Partial<Record<string, Component | string>> = {},
  fallback: Component | string = 'FaInput',
) {
  if (renderType === 'radio') {
    return options.length ? ElRadioGroup : ElRadio
  }
  if (renderType === 'checkbox') {
    return options.length ? ElCheckboxGroup : ElCheckbox
  }
  if (renderType === 'radioGroup' || renderType === 'radioButton') {
    return ElRadioGroup
  }
  if (renderType === 'checkboxGroup' || renderType === 'checkboxButton') {
    return ElCheckboxGroup
  }

  const component = overrides[renderType] ?? commonBuiltinComponentMap[renderType] ?? fallback
  return typeof component === 'string' ? resolveLocalComponent(component) : component
}

export function buildSchemaFieldOptionChildren(
  renderType: SchemaFieldBuiltinRender | string,
  fieldKey: string,
  options: SchemaFieldOptionLike[],
) {
  if (!options.length) {
    return undefined
  }

  if (renderType === 'select') {
    return () => options.map(option =>
      h(ElOption, {
        key: `${fieldKey}-${option.value}`,
        ...option,
      }))
  }

  if (renderType === 'radio' || renderType === 'radioGroup') {
    return () => options.map(option =>
      h(ElRadio, {
        key: `${fieldKey}-${option.value}`,
        value: option.value,
        disabled: option.disabled,
      }, () => option.label))
  }

  if (renderType === 'radioButton') {
    return () => options.map(option =>
      h(ElRadioButton, {
        key: `${fieldKey}-${option.value}`,
        value: option.value,
        disabled: option.disabled,
      }, () => option.label))
  }

  if (renderType === 'checkbox' || renderType === 'checkboxGroup') {
    return () => options.map(option =>
      h(ElCheckbox, {
        key: `${fieldKey}-${option.value}`,
        value: option.value,
        disabled: option.disabled,
      }, () => option.label))
  }

  if (renderType === 'checkboxButton') {
    return () => options.map(option =>
      h(ElCheckboxButton, {
        key: `${fieldKey}-${option.value}`,
        value: option.value,
        disabled: option.disabled,
      }, () => option.label))
  }

  return undefined
}

export function resolveSchemaFieldSharedExtraProps(
  renderType: SchemaFieldBuiltinRender | string,
  options: SchemaFieldOptionLike[],
) {
  if (renderType === 'textarea') {
    return { type: 'textarea' }
  }

  if (renderType === 'cascader') {
    return {
      options,
      teleported: true,
    }
  }

  if (renderType === 'selectV2') {
    return {
      options,
      teleported: true,
    }
  }

  if (renderType === 'treeSelect') {
    return {
      data: options,
      teleported: true,
    }
  }

  if (renderType === 'transfer') {
    return {
      data: options.map(option => ({
        ...option,
        key: option.value,
      })),
    }
  }

  return {}
}
